import * as React from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"

import Details from "Catalog/Edit/shared/components/Details"
import EditLayout from "Catalog/Edit/shared/components/EditLayout"
import useDelayedRenderedItem from "Catalog/Edit/shared/hooks/useDelayedRenderedItem"
import useCatalogNavigation from "Catalog/shared/hooks/useCatalogNavigation"
import useFindItemOrRedirect from "Catalog/shared/hooks/useFindItemOrRedirect"
import useKeyboardNavigation from "Catalog/shared/hooks/useKeyboardNavigation"
import useEditor from "shared/hooks/useEditor"
import { DealId } from "shared/hubrise/ids"
import { IDeal } from "shared/hubrise/models"
import * as Deal from "shared/hubrise/utils/Deal"

import Content from "./Content"
import Navigator from "./Navigator"
import Toolbar from "./Toolbar"

const Deals = (): JSX.Element => {
  const { t } = useTranslation()
  const params = useParams<{ dealId: string }>()

  const { catalog } = useEditor()
  const items = catalog.data.deals

  const { catalogNavigation, visit } = useCatalogNavigation()
  const indexPath = catalogNavigation.editPath("deals")

  const selectedItem = useFindItemOrRedirect<IDeal>(params.dealId, (id) => Deal.byId(catalog, DealId(id)), indexPath)

  const renderedItem = useDelayedRenderedItem(selectedItem)

  React.useEffect(() => {
    visit("deal", selectedItem)
  }, [selectedItem, visit])

  useKeyboardNavigation<IDeal>({
    item: selectedItem,
    collection: catalog.data.deals,
    indexPath: () => indexPath,
    showPath: (deal) => catalogNavigation.editPath("deal", deal.id),
  })

  return (
    <EditLayout
      toolbar={<Toolbar deal={renderedItem} />}
      navigator={items.length > 0 ? <Navigator items={items} selectedId={selectedItem?.id} /> : null}
      details={
        renderedItem ? (
          <Details.Main testId="catalog-details" resourceId={renderedItem.id}>
            <Content deal={renderedItem} />
          </Details.Main>
        ) : null
      }
      none={t("deal.none")}
      noneSelected={t("deal.none_selected")}
      testId="catalog-deals"
    />
  )
}

export default Deals
